/**
 * The signup / sign-in dialog.
 *
 * This replaces the dedicated /auth page as the primary way people authenticate.
 * The directory is public now, so there is nothing to be bounced away from. The
 * modal opens over the page the visitor is already reading, and closing it puts
 * them back exactly where they were.
 *
 * The field labels and placeholders, the button labels and the privacy line
 * are carried over from AuthPage verbatim. They are Caren's words, matched
 * character for character against the live Manus site; do not reword them.
 *
 * CHANGED, Aug 2026, deliberately and with Daniel's sign-off: the heading, the
 * eyebrow and the four benefit chips. The originals described the product
 * ("Free access to the app", "Access to the AI tools database") to someone who
 * had just answered two questions about their own work and was one click from
 * a specific answer. The replacements name what they are about to get. If
 * Caren wants the originals back they are in git history at 20af4e2.
 */

import { useEffect, useState } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { useLocation } from "wouter";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import { useAuthModal, type AuthModalMode } from "@/contexts/AuthModalContext";
import { safeRedirect } from "@/lib/safeRedirect";
import { AUTH_BULLETS, AUTH_COPY, AUTH_PRIVACY_LINE } from "@/lib/authCopy";

/**
 * What the account actually gives you.
 *
 * These used to read "Instant role-based recommendations", "Free access to the
 * app", "Access to the AI tools database" and "Explore the curated Resource
 * Center", which describe the product rather than anything the person in front
 * of the form asked for. Someone reaches this modal having just answered two
 * questions, so the first line names the thing they are one click from seeing.
 *
 * They live in lib/authCopy now, because /login shows the same four lines and
 * copy that exists in two files drifts.
 */
const BULLETS = AUTH_BULLETS;

export default function AuthModal() {
  const { isOpen, options, close } = useAuthModal();
  const [, setLocation] = useLocation();

  const [mode, setMode] = useState<AuthModalMode>("signup");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const utils = trpc.useUtils();
  const signupMutation = trpc.auth.signup.useMutation();
  const loginMutation = trpc.auth.login.useMutation();

  // Honour the tab the caller asked for each time the dialog is opened.
  useEffect(() => {
    if (isOpen) setMode(options.mode ?? "signup");
  }, [isOpen, options.mode]);

  const handleSuccess = async () => {
    await utils.auth.me.invalidate();
    if (options.returnTo) {
      /* SECURITY FIX, Aug 2026: this navigated to options.returnTo verbatim.
         /auth fills that value straight from its own query string, so
         /auth?returnTo=https://not-us.example was a working open redirect on a
         real sign-in form: a link genuinely from this domain, showing the
         genuine form, that dropped the person somewhere else the moment they
         authenticated. safeRedirect reduces it to a same-origin path or "/". */
      window.location.href = safeRedirect(options.returnTo);
      return;
    }
    // No navigation: the page underneath is still mounted, so the caller can
    // reveal whatever the account was needed for without a reload.
    options.onSuccess?.();
    setPassword("");
    close();
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim() || !password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setIsLoading(true);
    try {
      await signupMutation.mutateAsync({ firstName, email, password });
      toast.success("Welcome!");
      await handleSuccess();
    } catch (error: any) {
      toast.error(error?.message || "Signup failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setIsLoading(true);
    try {
      await loginMutation.mutateAsync({ email, password });
      toast.success("Welcome back!");
      await handleSuccess();
    } catch (error: any) {
      toast.error(error?.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * The heading follows the tab the visitor is on, not the one they arrived on.
   *
   * Callers pass copy written for the mode they open in: the header's "Log in"
   * link opens in login mode with "Welcome back". That copy was being held
   * across the tab switch, so someone who clicked Log in, realised they had no
   * account, and switched to Create Account was welcomed back to an account
   * they had not made yet.
   *
   * So the caller's copy applies while the modal is in the mode it was opened
   * in, and each mode has its own fallback for after a switch. Both fallbacks
   * are copy already in use: the signup pair is the existing default, and the
   * login pair is what AuthControl passes.
   */
  const openedIn = options.mode ?? "signup";

  /* Shared with /login via lib/authCopy, so the dialog and the page cannot
     start describing different products. */
  const FALLBACK_COPY = AUTH_COPY;

  /** Bound once: TypeScript loses the narrowing inside a map callback. */
  const result = options.result;

  const callerCopyApplies = mode === openedIn;
  const title =
    (callerCopyApplies ? options.title : undefined) ?? FALLBACK_COPY[mode].title;
  const description =
    (callerCopyApplies ? options.description : undefined) ??
    FALLBACK_COPY[mode].description;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && close()}>
      <DialogContent
        className="max-w-[calc(100%-1.5rem)] sm:max-w-4xl max-h-[92vh] overflow-y-auto bg-[#f5f0e8] p-5 sm:p-7"
      >
        <div>
          <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">
            Your Smart Stack
          </p>
          <DialogTitle className="font-['Fraunces'] text-xl sm:text-2xl font-bold text-foreground leading-tight pr-8">
            {title}
          </DialogTitle>
          <DialogDescription className="mt-1.5">{description}</DialogDescription>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_340px] gap-5 md:gap-7 items-start">
          {/* LEFT: the quiz result if there is one, otherwise the bullets.
              The overview video that used to sit here was removed on 2 Sep
              2026 at Caren's direction. With it went the off-screen parking
              node in AuthModalProvider, which existed only to keep that one
              player alive across open/close cycles. */}
          <div className="flex flex-col gap-3">
            {result && (
              <div className="rounded-xl border border-border bg-card p-4 sm:p-5">
                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                  Your result is ready
                </p>
                <p className="font-['Fraunces'] text-xl font-bold text-foreground mt-1">
                  Your platform to start with is ready
                </p>
                <p className="text-sm text-muted-foreground mt-1.5">
                  You said:{" "}
                  <span className="font-semibold text-foreground">
                    {result.roleLabel}
                  </span>{" "}
                  &middot; {result.intentLabel}
                </p>
                <div className="grid gap-2 mt-4" aria-hidden="true">
                  {Array.from({ length: result.count }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-dashed border-border bg-muted/40 p-3 flex items-center gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="h-3 w-2/3 rounded bg-foreground/10 mb-2" />
                        <div className="h-2 w-full rounded bg-foreground/5" />
                      </div>
                      <span
                        className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full border ${result.chipClass}`}
                      >
                        {result.roleLabel}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 ${result ? "hidden" : ""}`}>
              {BULLETS.map(bullet => (
                <div
                  key={bullet}
                  className="flex items-center gap-2.5 bg-white border border-[#e8e0d4] rounded-lg px-3 py-2 shadow-sm"
                >
                  <CheckCircle2 className="w-4 h-4 text-[oklch(0.38_0.09_155)] shrink-0" />
                  <span
                    className="font-medium text-foreground/80 leading-tight"
                    style={{ fontSize: 16 }}
                  >
                    {bullet}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: the form
              Sign In has one field fewer than Create Account, so switching
              tabs used to shrink the whole dialog by ~50px: the panel measured
              454px on signup and 386px on login, and everything below it
              jumped up. Toggling between two views of the same thing should
              not move the thing.
              The panel holds the taller of the two heights and the form is
              centred in the space under the tabs, so only the fields change.
              470px is the signup panel's natural height, measured. It is not
              derived from anything, so it goes stale whenever the copy or the
              fields above it change: it was 454 until the tabs went from 36px
              to 44px for the tap target. To re-measure, open the dialog, read
              the panel's height on the Create Account tab, and put that number
              here. If the dialog visibly jumps when you switch tabs, this is
              why.
              Height is held at md and up, where the two-column layout applies;
              on a phone the panel is stacked and full width, and reserving
              empty space there would just be a gap. */}
          <div className="bg-white rounded-xl border border-[#e8e0d4] shadow-[0_4px_24px_rgba(0,0,0,0.07)] p-5 flex flex-col md:min-h-[470px]">
            {/* min-h-11 rather than py-2: these came out 36px, which is
                under the 44px floor the rest of the site meets, and they are
                the control that decides whether someone signs up or signs
                in. */}
            <div className="flex rounded-lg border border-[#e8e0d4] overflow-hidden mb-5">
              <button
                type="button"
                onClick={() => setMode("signup")}
                className={`flex-1 inline-flex items-center justify-center min-h-11 text-sm font-semibold transition-colors ${
                  mode === "signup"
                    ? "bg-primary text-primary-foreground"
                    : "bg-[#f5f0e8] text-muted-foreground hover:text-foreground"
                }`}
              >
                Create Account
              </button>
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`flex-1 inline-flex items-center justify-center min-h-11 text-sm font-semibold transition-colors ${
                  mode === "login"
                    ? "bg-primary text-primary-foreground"
                    : "bg-[#f5f0e8] text-muted-foreground hover:text-foreground"
                }`}
              >
                Sign In
              </button>
            </div>

            {/* Centred in whatever height is left under the tabs, so the
                shorter Sign In form sits in the middle rather than leaving a
                block of white space beneath it. */}
            <div className="flex-1 flex flex-col justify-center">
            {mode === "signup" ? (
              <>
                <h2 className="font-['Fraunces'] text-lg font-semibold text-foreground mb-0.5">
                  Create your free account
                </h2>
                <p className="text-xs text-muted-foreground mb-4">
                  Instant access to the AI tools database.
                </p>
                <form onSubmit={handleSignup} className="space-y-3">
                  <div>
                    <label htmlFor="modal-firstName" className="block text-xs font-medium text-foreground mb-1">
                      First Name
                    </label>
                    <Input
                      id="modal-firstName"
                      type="text"
                      placeholder="Your first name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      disabled={isLoading}
                      className="w-full bg-white h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="modal-signup-email" className="block text-xs font-medium text-foreground mb-1">
                      Email
                    </label>
                    <Input
                      id="modal-signup-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      disabled={isLoading}
                      className="w-full bg-white h-9 text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="modal-signup-password" className="block text-xs font-medium text-foreground mb-1">
                      Password
                    </label>
                    <Input
                      id="modal-signup-password"
                      type="password"
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={isLoading}
                      className="w-full bg-white h-9 text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full ${BUTTON.primary}`}
                  >
                    {isLoading ? "Creating account..." : (
                      <>
                        {/* Names the thing they are about to get when we know
                            it. "Get Instant Access" is accurate everywhere but
                            says nothing at the one moment we can be specific. */}
                        {result ? "Show me my answer" : "Get Instant Access"}{" "}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              <>
                {/* Was "Welcome Back", which repeated the dialog heading two
                    lines above it in a different capitalisation. Naming the
                    form instead mirrors "Create your free account" on the
                    other tab, so the two modes read as the same panel with
                    different fields. */}
                <h2 className="font-['Fraunces'] text-lg font-semibold text-foreground mb-0.5">
                  Sign in to your account
                </h2>
                <p className="text-xs text-muted-foreground mb-4">
                  Your stack and your progress are waiting.
                </p>
                <form onSubmit={handleLogin} className="space-y-3">
                  <div>
                    <label htmlFor="modal-login-email" className="block text-xs font-medium text-foreground mb-1">
                      Email
                    </label>
                    <Input
                      id="modal-login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      disabled={isLoading}
                      className="w-full bg-white h-9 text-sm"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label htmlFor="modal-login-password" className="block text-xs font-medium text-foreground">
                        Password
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          close();
                          setLocation("/auth/forgot-password");
                        }}
                        className="text-xs text-primary hover:underline font-medium"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="modal-login-password"
                      type="password"
                      placeholder="Your password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={isLoading}
                      className="w-full bg-white h-9 text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`w-full ${BUTTON.primary}`}
                  >
                    {isLoading ? "Signing in..." : (
                      <>Sign In <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </form>
              </>
            )}
            </div>

            <p className="text-xs text-muted-foreground text-center mt-4">
              {AUTH_PRIVACY_LINE}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
